<template>
  <div class="msu-page table-def-pg">
    <Search />
    <div class="breadcrumb-wrap">
      <div class="container">
        <v-breadcrumbs :items="breadcrumbs" />
      </div>
    </div>
    <div class="container">
      <div class="position">
        <div class="bg-white p-20">
          <div class="msu-heading">
            <div>
              <h1>{{ $t('Проекты НПА местного самоуправления') }}</h1>
              <p>{{ $t('Реестр органов МСУ и опубликованных ими проектов нормативных правовых актов') }}</p>
            </div>
          </div>

          <v-row class="msu-summary">
            <v-col cols="12" sm="4">
              <div class="msu-stat"><strong>{{ summary.territoryCount || 0 }}</strong><span>{{ $t('Территории МСУ') }}</span></div>
            </v-col>
            <v-col cols="12" sm="4">
              <div class="msu-stat"><strong>{{ summary.authorityCount || 0 }}</strong><span>{{ $t('Органы МСУ') }}</span></div>
            </v-col>
            <v-col cols="12" sm="4">
              <div class="msu-stat">
                <router-link
                  class="msu-stat-link"
                  :to="$i18nRoute({ name: 'search-npa', query: { msu: 1 } })"
                  :title="$t('Опубликованные проекты')"
                >
                  <strong>{{ summary.projectCount || 0 }}</strong>
                </router-link>
                <span>{{ $t('Опубликованные проекты') }}</span>
              </div>
            </v-col>
          </v-row>

          <v-tabs v-model="tab" class="mb-5">
            <v-tab>{{ $t('Реестр') }}</v-tab>
            <v-tab>{{ $t('Карта') }}</v-tab>
          </v-tabs>

          <div class="search-action msu-filters">
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field v-model="filters.name" :label="$t('Наименование или код СОАТЕ')" outlined dense clearable @keyup.enter="applyFilters" />
              </v-col>
              <v-col cols="12" sm="6" md="2">
                <v-select v-model="filters.regionCode" :items="regions" item-value="code" item-text="transName" :label="$t('Область')" outlined dense clearable @change="onRegionChange" />
              </v-col>
              <v-col cols="12" sm="6" md="2">
                <v-select v-model="filters.districtCode" :items="availableDistricts" item-value="code" item-text="transName" :label="$t('Район')" outlined dense clearable />
              </v-col>
              <v-col cols="12" sm="6" md="2">
                <v-select v-model="filters.territoryType" :items="territoryTypes" item-value="id" item-text="transName" :label="$t('Тип территории')" outlined dense clearable />
              </v-col>
              <v-col cols="12" sm="6" md="2">
                <v-select v-model="filters.authorityType" :items="authorityTypes" item-value="id" item-text="transName" :label="$t('Тип органа')" outlined dense clearable />
              </v-col>
            </v-row>
            <div class="form-action">
              <button class="btn btn-default blue" @click="applyFilters">{{ $t('Поиск') }}</button>
              <button class="btn btn-default white" @click="clearFilters">{{ $t('Очистить') }}</button>
            </div>
          </div>

          <div v-if="tab === 0" class="search-result-body mt-5">
            <div class="type-column">
              <v-simple-table>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th v-for="item in tableHeaders" :key="item.key">
                        <Sorting
                          :item="item"
                          :currentOrderKey="orderBy"
                          @order="sortData"
                          @cancelSorting="cancelSorting"
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in authorities" :key="item.id">
                      <td>
                        <router-link :to="$i18nRoute({ name: 'msu-authority', params: { id: item.id } })">
                          {{ item.name }}
                        </router-link>
                        <small class="d-block">СОАТЕ: {{ item.soateCode }}</small>
                      </td>
                      <td>{{ authorityTypeName(item.authorityType) }}</td>
                      <td>{{ item.regionName }}</td>
                      <td>{{ item.districtName || '—' }}</td>
                      <td>{{ item.projectCount }}</td>
                    </tr>
                    <tr v-if="authorities.length === 0">
                      <td colspan="5" class="text-center">{{ $t('По вашему запросу ничего не найдено') }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
            <div class="search-result-footer">
              <p>{{ $t('Общее количество') }}: {{ paginationData.totalCount || 0 }}</p>
            </div>
            <v-pagination v-if="paginationData.pageCount > 1" v-model="page" :length="paginationData.pageCount" :total-visible="7" @input="loadAuthorities" />
          </div>
          <MsuMap v-else :items="mapItems" class="mt-5" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex'
import Search from '@/components/Search'
import Sorting from '@/components/app/Sorting'
import MsuMap from '@/components/msu/MsuMap'
import Msu from '@/model/msu'
import { Trans } from '@/plugins/Translation'

export default {
  components: { Search, Sorting, MsuMap },
  data: () => ({
    tab: 0,
    page: 1,
    orderBy: 'projectCount',
    orderDir: 'desc',
    filters: {},
    authorities: [],
    mapItems: [],
    regions: [],
    districts: [],
    territoryTypes: [],
    authorityTypes: [],
    summary: {},
    paginationData: {},
    breadcrumbs: []
  }),
  computed: {
    tableHeaders() {
      return [
        { name: this.$t('Орган МСУ'), key: 'name' },
        { name: this.$t('Тип'), key: 'authorityType' },
        { name: this.$t('Область'), key: 'regionName' },
        { name: this.$t('Район'), key: 'districtName' },
        { name: this.$t('Проекты'), key: 'projectCount' }
      ]
    },
    availableDistricts() {
      if (!this.filters.regionCode) return this.districts
      return this.districts.filter(item => item.regionCode === this.filters.regionCode)
    }
  },
  watch: {
    tab(value) {
      if (value === 1) this.loadMap()
    }
  },
  created() {
    this.breadcrumbs = [
      { text: this.$i18n.t('Главная'), disabled: false, href: Trans.currentLocaleUrl + '/' },
      { text: this.$i18n.t('Проекты МСУ'), disabled: true }
    ]
    this.loadInitialData()
  },
  methods: {
    ...mapMutations(['setLoading']),
    async loadInitialData() {
      this.setLoading(true)
      try {
        const response = await Msu.getReferences()
        Object.assign(this, response.data)
        await this.loadAuthorities(false)
      } finally {
        this.setLoading(false)
      }
    },
    async loadAuthorities(showLoading = true) {
      if (showLoading) this.setLoading(true)
      try {
        const response = await Msu.getAuthorities({
          ...this.filters,
          page: this.page,
          limit: 20,
          orderBy: this.orderBy,
          orderDir: this.orderDir
        })
        this.authorities = response.data.items
        this.paginationData = response.data.paginationData
      } finally {
        if (showLoading) this.setLoading(false)
      }
    },
    async loadMap() {
      this.setLoading(true)
      try {
        const response = await Msu.getMap(this.filters)
        this.mapItems = response.data
      } finally {
        this.setLoading(false)
      }
    },
    applyFilters() {
      this.page = 1
      return this.tab === 0 ? this.loadAuthorities() : this.loadMap()
    },
    clearFilters() {
      this.filters = {}
      this.applyFilters()
    },
    onRegionChange() {
      if (!this.availableDistricts.some(item => item.code === this.filters.districtCode)) {
        this.$delete(this.filters, 'districtCode')
      }
    },
    sortData(sort) {
      this.orderBy = sort.key
      this.orderDir = sort.value
      this.page = 1
      this.loadAuthorities()
    },
    cancelSorting() {
      this.orderBy = ''
      this.orderDir = 'desc'
      this.page = 1
      this.loadAuthorities()
    },
    authorityTypeName(id) {
      const type = this.authorityTypes.find(item => item.id === id)
      return type ? type.transName : id
    }
  }
}
</script>

<style scoped>
.msu-heading { display: flex; flex-direction: column; align-items: flex-start; gap: 4px; margin-bottom: 12px; }
.msu-heading h1 { margin-bottom: 8px; }
.msu-summary { margin-bottom: 12px; }
.msu-stat { border: 1px solid #e1e1e1; padding: 20px; height: 100%; }
.msu-stat strong { color: #004581; display: block; font-size: 30px; line-height: 1.2; }
.msu-stat span { color: #49525d; }
.msu-stat-link { display: inline-block; text-decoration: underline; text-decoration-thickness: 2px; text-underline-offset: 3px; }
.msu-stat-link:hover strong, .msu-stat-link:focus strong { color: #1976d2; }
.msu-filters { padding-top: 20px; }
.msu-filters .v-input { margin-bottom: -16px; }
.type-column { overflow-x: auto; }
small { color: #777; margin-top: 4px; }
</style>
